import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { v4 as uuidv4 } from "uuid";
import { Badge, Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap";
import { AuthContext } from "../../../contexts/AuthContext";

// (2026-10) Consumos y necesidades de tela de un pedido (ERP-MOD-TELAS-2026).
// Lo usan ListadoPedidos y ListadoOrdenesCorteExterno.
// REQ-01 la tela del pedido viene ya puesta (cambiarla pide ped_consumoCambiarTela)
// REQ-02 el consumo unitario se completa con el último del mismo artículo + tela
// REQ-03 varios insumos por color (forro, entretela, combinación...) con su función
// REQ-04 cada insumo contra la existencia libre (rollos sin producción/apartado/venta)
// REQ-05 matriz resumen de lo que hay que comprar

const URL = process.env.REACT_APP_URL_CONSUMO_TELAS;
const auth = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });

export const TIPOS_INSUMO = ["Tela Principal", "Tela de Combinación", "Forro", "Entretela", "Pellón", "Sesgo", "Otro"];
// tipo de la tela en el catálogo → función sugerida en la prenda
const tipoPorCatalogo = (tela) => {
  const t = String(tela?.tipo || "").trim().toUpperCase();
  if (t === "FORRO") return "Forro";
  if (t === "ENTRETELA") return "Entretela";
  if (t === "PELLON") return "Pellón";
  return "";
};
const m2 = (n) => Math.round(Number(n || 0) * 100) / 100;
const fmt = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(Number(n || 0));
const num = (v) => (v === "" || v == null || isNaN(Number(v)) ? 0 : Number(v));

function ConsumosTelasModal({ isOpen, toggle, idPedido, numeroPedido, cliente, onPDF }) {
  const { user } = useContext(AuthContext);
  const puedeCambiarTela = !!user?.ped_consumoCambiarTela;

  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [datos, setDatos] = useState(null);
  const [colores, setColores] = useState([]);
  const [filas, setFilas] = useState([]);

  const telaDe = (id) => (datos?.telas || []).find((t) => String(t._id) === String(id));
  const colorDe = (id) => colores.find((c) => String(c._id) === String(id));
  const historicoDe = (idArticulo, idTela) => datos?.historico?.[`${idArticulo}|${idTela}`];

  // ---- carga
  const cargar = async () => {
    if (!idPedido) return;
    setCargando(true);
    try {
      const [{ data }, resColores] = await Promise.all([
        axios.get(`${URL}Proyeccion/${idPedido}`, auth()),
        colores.length ? Promise.resolve({ data: colores }) : axios.get(process.env.REACT_APP_URL_COLORES, auth()),
      ]);
      setColores(Array.isArray(resColores.data) ? resColores.data : []);
      setDatos(data);
      // filas guardadas + una por cada tela del pedido que aún no tenga consumo
      const nuevas = (data.consumos || []).map((c) => ({ ...c, key: c._id, consumo: c.consumo || "" }));
      (data.articulos || []).forEach((art) => {
        art.colores.forEach((col) => {
          const yaHay = (idTela) =>
            nuevas.some((f) => f.idArticulo === art.idArticulo && f.colores === col.idColor && (!idTela || f.telas === idTela));
          if (col.telas.length) {
            col.telas.forEach((t) => {
              // tela fuera del catálogo: sólo si ese color todavía no tiene nada
              if (t.idTela ? yaHay(t.idTela) : yaHay("")) return;
              const hist = t.idTela ? data.historico?.[`${art.idArticulo}|${t.idTela}`] : null;
              const cat = (data.telas || []).find((x) => String(x._id) === String(t.idTela));
              nuevas.push({
                key: uuidv4(),
                idArticulo: art.idArticulo,
                colores: col.idColor,
                telas: t.idTela || "",
                coloresTela: "",
                tipoInsumo: "Tela Principal",
                consumo: hist ? hist.consumo : "",
                ancho: (hist && hist.ancho) || cat?.ancho || "",
                observaciones: "",
                deHistorico: !!hist,
              });
            });
          } else if (!yaHay("")) {
            nuevas.push({
              key: uuidv4(),
              idArticulo: art.idArticulo,
              colores: col.idColor,
              telas: "",
              coloresTela: "",
              tipoInsumo: "Tela Principal",
              consumo: "",
              ancho: "",
              observaciones: "",
            });
          }
        });
      });
      setFilas(nuevas);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "No se pudo cargar la información del pedido", "error");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (isOpen) cargar();
    else {
      setDatos(null);
      setFilas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, idPedido]);

  // ---- reglas de cada fila
  const colorDelPedido = (fila) => {
    const art = (datos?.articulos || []).find((a) => a.idArticulo === fila.idArticulo);
    return art?.colores.find((c) => c.idColor === fila.colores);
  };
  // la tela es la que trae el pedido para ese artículo y color
  const telaDelPedido = (fila) => !!fila.telas && !!colorDelPedido(fila)?.telas.some((t) => t.idTela === fila.telas);
  const bloqueada = (fila) => telaDelPedido(fila) && fila.tipoInsumo === "Tela Principal" && !puedeCambiarTela;
  // piezas: las de esa tela si es tela del pedido; si no, todo el color (forro,
  // entretela... van en toda la prenda); sin color, todo el artículo
  const piezasDe = (fila) => {
    const col = colorDelPedido(fila);
    if (!col) return (datos?.articulos || []).find((a) => a.idArticulo === fila.idArticulo)?.cantidad || 0;
    const t = col.telas.find((x) => x.idTela && x.idTela === fila.telas);
    return t ? t.cantidad : col.cantidad;
  };
  const colorStockDe = (fila) => fila.coloresTela || fila.colores || "";
  const llaveStock = (fila) => `${fila.telas}|${colorStockDe(fila)}`;
  const requeridoDe = (fila) => m2(num(fila.consumo) * piezasDe(fila));

  const cambia = (key, campo, valor) =>
    setFilas((prev) =>
      prev.map((f) => {
        if (f.key !== key) return f;
        const n = { ...f, [campo]: valor };
        if (campo === "telas") {
          const cat = telaDe(valor);
          const hist = historicoDe(f.idArticulo, valor);
          n.ancho = (hist && hist.ancho) || cat?.ancho || "";
          const sugerido = tipoPorCatalogo(cat);
          if (sugerido) n.tipoInsumo = sugerido;
          if (!num(f.consumo) && hist) {
            n.consumo = hist.consumo;
            n.deHistorico = true;
          }
        }
        if (campo === "consumo") n.deHistorico = false;
        return n;
      })
    );

  const agregarInsumo = (idArticulo, idColor) =>
    setFilas((prev) => [
      ...prev,
      {
        key: uuidv4(),
        idArticulo,
        colores: idColor,
        telas: "",
        coloresTela: "",
        tipoInsumo: "Tela de Combinación",
        consumo: "",
        ancho: "",
        observaciones: "",
      },
    ]);

  const quitar = async (fila) => {
    if (fila._id) {
      const r = await Swal.fire({
        title: "¿Borrar este insumo?",
        text: `${telaDe(fila.telas)?.nombre || "Sin tela"} · ${fila.tipoInsumo}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, borrar",
        cancelButtonText: "No",
      });
      if (!r.isConfirmed) return;
      try {
        await axios.delete(`${URL}/${fila._id}`, auth());
      } catch (e) {
        return Swal.fire("Error", "No se pudo borrar", "error");
      }
    }
    setFilas((prev) => prev.filter((f) => f.key !== fila.key));
  };

  // ---- REQ-04 / REQ-05: requerido contra existencia libre por tela + color
  const resumen = useMemo(() => {
    const grupos = {};
    filas.forEach((f) => {
      if (!f.telas) return;
      const k = llaveStock(f);
      if (!grupos[k]) {
        const st = datos?.stock?.[k] || (colorStockDe(f) ? null : datos?.stock?.[`${f.telas}|*`]);
        grupos[k] = {
          llave: k,
          material: telaDe(f.telas)?.nombre || "Tela",
          tipos: new Set(),
          color: colorDe(colorStockDe(f))?.name || (colorStockDe(f) ? "" : "Todos"),
          requerido: 0,
          stock: st?.metros || 0,
          rollos: st?.rollos || 0,
        };
      }
      grupos[k].tipos.add(f.tipoInsumo || "Tela Principal");
      grupos[k].requerido = m2(grupos[k].requerido + requeridoDe(f));
    });
    return Object.values(grupos)
      .map((g) => ({ ...g, tipos: [...g.tipos].join(" / "), faltan: m2(Math.max(0, g.requerido - g.stock)) }))
      .sort((a, b) => b.faltan - a.faltan || a.material.localeCompare(b.material, "es"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filas, datos, colores]);
  const estadoDe = (fila) => resumen.find((g) => g.llave === llaveStock(fila));

  // ---- guardar
  const guardar = async () => {
    const validas = filas.filter((f) => f.telas);
    if (!validas.length) return Swal.fire("Sin insumos", "Elige la tela de al menos un renglón", "warning");
    setGuardando(true);
    try {
      await Promise.all(
        validas.map((f) => {
          const payload = {
            articulos: f.idArticulo,
            telas: f.telas,
            pedidos: idPedido,
            tipoInsumo: f.tipoInsumo || "Tela Principal",
            observaciones: String(f.observaciones || "").trim() || "NA",
            cantidad: num(f.consumo),
            ancho: num(f.ancho),
            ...(f.colores ? { colores: f.colores } : {}),
            ...(f.coloresTela ? { coloresTela: f.coloresTela } : {}),
          };
          return f._id ? axios.patch(`${URL}/${f._id}`, payload, auth()) : axios.post(URL, payload, auth());
        })
      );
      Swal.fire("Listo", "Consumos guardados", "success");
      await cargar();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudieron guardar todos los consumos", "error");
    } finally {
      setGuardando(false);
    }
  };

  // ---- PDF de la matriz de compras (REQ-05) con el detalle por artículo
  const pdfResumen = () => {
    const doc = new jsPDF("landscape");
    const azul = [0, 58, 92];
    doc.setFont(undefined, "bold").setFontSize(15).setTextColor(...azul);
    doc.text("CONSUMOS Y NECESIDADES DE TELA", 14, 16);
    doc.setFont(undefined, "normal").setFontSize(10).setTextColor(60);
    doc.text(`Pedido ${numeroPedido || datos?.pedido?.idPedido || ""} · ${cliente || datos?.pedido?.cliente || ""}`, 14, 23);
    doc.setTextColor(0);
    const detalle = [];
    (datos?.articulos || []).forEach((art) => {
      filas
        .filter((f) => f.idArticulo === art.idArticulo && f.telas)
        .forEach((f) =>
          detalle.push([
            art.articulo,
            colorDe(f.colores)?.name || "",
            f.tipoInsumo,
            telaDe(f.telas)?.nombre || "",
            colorDe(f.coloresTela)?.name || "",
            fmt(f.consumo),
            fmt(piezasDe(f)),
            `${fmt(requeridoDe(f))} m`,
          ])
        );
    });
    doc.autoTable({
      startY: 28,
      head: [["ARTÍCULO", "COLOR", "FUNCIÓN", "TELA / INSUMO", "COLOR TELA", "CONSUMO U.", "PIEZAS", "METROS"]],
      body: detalle,
      styles: { fontSize: 8.5, cellPadding: 1.6 },
      headStyles: { fillColor: azul },
      columnStyles: { 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" } },
    });
    doc.autoTable({
      startY: doc.autoTable.previous.finalY + 8,
      head: [["MATERIAL / INSUMO", "TIPO / FUNCIÓN", "COLOR", "METROS REQ.", "STOCK ACTUAL", "ESTADO"]],
      body: resumen.map((g) => [
        g.material,
        g.tipos,
        g.color,
        `${fmt(g.requerido)} m`,
        `${fmt(g.stock)} m`,
        g.faltan > 0 ? `PEDIR ${fmt(g.faltan)} M` : "SURTIDO OK",
      ]),
      styles: { fontSize: 9, cellPadding: 1.8 },
      headStyles: { fillColor: azul },
      columnStyles: { 3: { halign: "right" }, 4: { halign: "right" }, 5: { fontStyle: "bold" } },
      didParseCell: (h) => {
        if (h.section === "body" && h.column.index === 5) {
          h.cell.styles.textColor = String(h.cell.raw).startsWith("PEDIR") ? [185, 28, 28] : [22, 101, 52];
        }
      },
      rowPageBreak: "avoid",
    });
    doc.save(`Consumos pedido ${numeroPedido || ""}.pdf`);
  };

  // ---- vista
  const encabezado = (
    <tr className="text-center" style={{ fontSize: 12, fontWeight: 600, color: "#52514e" }}>
      <td style={{ width: "13%" }}>Función</td>
      <td style={{ width: "18%" }}>Tela / insumo</td>
      <td style={{ width: "13%" }}>Color tela</td>
      <td style={{ width: "7%" }}>Ancho</td>
      <td style={{ width: "9%" }}>Consumo u. (m)</td>
      <td style={{ width: "6%" }}>Piezas</td>
      <td style={{ width: "8%" }}>Total (m)</td>
      <td style={{ width: "11%" }}>Existencia</td>
      <td style={{ width: "11%" }}>Observaciones</td>
      <td style={{ width: "4%" }}></td>
    </tr>
  );
  const renglon = (f) => {
    const est = estadoDe(f);
    const hist = f.telas ? historicoDe(f.idArticulo, f.telas) : null;
    const cand = bloqueada(f);
    return (
      <tr key={f.key}>
        <td>
          <Input
            bsSize="sm"
            type="select"
            value={f.tipoInsumo || "Tela Principal"}
            disabled={cand}
            onChange={(e) => cambia(f.key, "tipoInsumo", e.target.value)}
          >
            {TIPOS_INSUMO.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Input>
        </td>
        <td>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Input
              bsSize="sm"
              type="select"
              value={f.telas}
              disabled={cand}
              title={cand ? "Es la tela del pedido: cambiarla requiere permiso de supervisión" : undefined}
              onChange={(e) => cambia(f.key, "telas", e.target.value)}
            >
              <option value="">-- Tela --</option>
              {(datos?.telas || [])
                .filter((t) => t.is_active !== "No" || String(t._id) === String(f.telas))
                .map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.nombre}
                    {t.tipo && t.tipo.toUpperCase() !== "TELA" && t.tipo.toUpperCase() !== "NA" ? ` (${t.tipo})` : ""}
                  </option>
                ))}
            </Input>
            {cand ? <i className="fas fa-lock" style={{ color: "#8a8a8a" }} title="Tela del pedido (requiere supervisión)"></i> : null}
          </div>
        </td>
        <td>
          <Input bsSize="sm" type="select" value={f.coloresTela || ""} onChange={(e) => cambia(f.key, "coloresTela", e.target.value)}>
            <option value="">(el de la prenda)</option>
            {colores.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </Input>
        </td>
        <td>
          <Input bsSize="sm" type="number" step="any" value={f.ancho || ""} onChange={(e) => cambia(f.key, "ancho", e.target.value)} />
        </td>
        <td>
          <Input
            bsSize="sm"
            type="number"
            step="any"
            min="0"
            value={f.consumo}
            onChange={(e) => cambia(f.key, "consumo", e.target.value)}
            title={hist ? `Histórico: ${hist.consumo} m (pedido ${hist.pedido}, ${hist.fecha})` : undefined}
            style={f.deHistorico ? { background: "#eef6ff" } : undefined}
          />
          {f.deHistorico && hist ? <small className="text-muted">histórico ped. {hist.pedido}</small> : null}
        </td>
        <td className="text-center">{fmt(piezasDe(f))}</td>
        <td className="text-center" style={{ fontWeight: 700 }}>
          {fmt(requeridoDe(f))}
        </td>
        <td className="text-center" style={{ fontSize: 12 }}>
          {f.telas && est ? (
            <>
              <div>{fmt(est.stock)} m libres</div>
              {est.faltan > 0 ? (
                <Badge color="danger">Faltan {fmt(est.faltan)} m</Badge>
              ) : (
                <Badge color="success">En stock</Badge>
              )}
            </>
          ) : (
            "-"
          )}
        </td>
        <td>
          <Input bsSize="sm" value={f.observaciones || ""} onChange={(e) => cambia(f.key, "observaciones", e.target.value)} />
        </td>
        <td className="text-center">
          <Button size="sm" color="danger" disabled={cand} title="Quitar insumo" onClick={() => quitar(f)}>
            <i className="fas fa-trash"></i>
          </Button>
        </td>
      </tr>
    );
  };

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle} style={{ maxWidth: "1320px", width: "96%" }}>
      <ModalHeader toggle={toggle}>
        Consumos Pedido {numeroPedido || datos?.pedido?.idPedido || ""}
        {cliente || datos?.pedido?.cliente ? ` · ${cliente || datos?.pedido?.cliente}` : ""}
      </ModalHeader>
      <ModalBody>
        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          {onPDF ? (
            <Button size="sm" color="danger" onClick={onPDF}>
              <i className="far fa-file-pdf"></i> PDF Consumos
            </Button>
          ) : null}
          <Button size="sm" color="danger" onClick={pdfResumen} disabled={!resumen.length}>
            <i className="far fa-file-pdf"></i> PDF Necesidades de tela
          </Button>
          {!puedeCambiarTela ? (
            <small className="text-muted" style={{ alignSelf: "center" }}>
              <i className="fas fa-lock"></i> La tela que viene del pedido sólo se cambia con permiso de supervisión.
            </small>
          ) : null}
        </div>

        {cargando || !datos ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            <Table size="sm" borderless className="mb-3">
              <tbody>
                {datos.articulos.map((art) => {
                  const sinColor = filas.filter((f) => f.idArticulo === art.idArticulo && !art.colores.some((c) => c.idColor === f.colores));
                  return (
                    <React.Fragment key={art.idArticulo}>
                      <tr className="table-active">
                        <td colSpan="8" style={{ fontWeight: 700 }}>
                          {art.articulo}
                        </td>
                        <td colSpan="2" className="text-end" style={{ fontWeight: 700 }}>
                          {fmt(art.cantidad)} pzas
                        </td>
                      </tr>
                      {art.colores.map((col) => {
                        const deEsteColor = filas.filter((f) => f.idArticulo === art.idArticulo && f.colores === col.idColor);
                        const sinCatalogo = col.telas.filter((t) => !t.idTela);
                        return (
                          <React.Fragment key={`${art.idArticulo}-${col.idColor}`}>
                            <tr style={{ backgroundColor: "#eef3f8" }}>
                              <td colSpan="8">
                                <b>Color: {col.color}</b>
                                <span className="text-muted"> — {fmt(col.cantidad)} pzas</span>
                                {col.telas.length ? (
                                  <span className="text-muted" style={{ fontSize: 12 }}>
                                    {" "}
                                    · tela del pedido: {col.telas.map((t) => `${t.telaPedido} (${fmt(t.cantidad)})`).join(", ")}
                                  </span>
                                ) : (
                                  <span className="text-muted" style={{ fontSize: 12 }}> · el pedido no trae tela</span>
                                )}
                                {sinCatalogo.length ? (
                                  <span style={{ fontSize: 12, color: "#b45309" }}>
                                    {" "}
                                    ⚠ {sinCatalogo.map((t) => t.telaPedido).join(", ")} no está en el catálogo de telas
                                  </span>
                                ) : null}
                              </td>
                              <td colSpan="2" className="text-end">
                                <Button size="sm" color="success" onClick={() => agregarInsumo(art.idArticulo, col.idColor)}>
                                  + Insumo
                                </Button>
                              </td>
                            </tr>
                            {encabezado}
                            {deEsteColor.map(renglon)}
                          </React.Fragment>
                        );
                      })}
                      {sinColor.length ? (
                        <>
                          <tr style={{ backgroundColor: "#fdf0f0" }}>
                            <td colSpan="10" style={{ fontWeight: 700, color: "#b91c1c" }}>
                              Consumos sin color de prenda (capturados antes) — se calculan con todas las piezas del artículo
                            </td>
                          </tr>
                          {encabezado}
                          {sinColor.map(renglon)}
                        </>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>

            {/* REQ-05 matriz de compras del pedido */}
            <h5 style={{ color: "#003A5C" }}>Resumen de insumos del pedido</h5>
            {resumen.length ? (
              <Table size="sm" striped bordered>
                <thead>
                  <tr>
                    <th>Material / insumo</th>
                    <th>Tipo / función</th>
                    <th>Color</th>
                    <th className="text-end">Metros req.</th>
                    <th className="text-end">Stock actual</th>
                    <th className="text-center">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {resumen.map((g) => (
                    <tr key={g.llave}>
                      <td style={{ fontWeight: 600 }}>{g.material}</td>
                      <td>{g.tipos}</td>
                      <td>{g.color}</td>
                      <td className="text-end">{fmt(g.requerido)} m</td>
                      <td className="text-end">
                        {fmt(g.stock)} m{g.rollos ? <small className="text-muted"> ({g.rollos} rollos)</small> : null}
                      </td>
                      <td className="text-center">
                        {g.faltan > 0 ? (
                          <Badge color="danger">PEDIR {fmt(g.faltan)} M</Badge>
                        ) : (
                          <Badge color="success">SURTIDO OK</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className="text-muted">Elige la tela de los renglones para ver lo que se necesita.</p>
            )}
            <small className="text-muted">
              Existencia = rollos libres (sin asignar a producción, sin apartar y sin vender) de esa tela en el color de la tela; si no se elige
              color de tela, se toma el color de la prenda.
            </small>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={guardar} disabled={guardando || cargando}>
          {guardando ? "Guardando..." : "Guardar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default ConsumosTelasModal;
